import { Component } from '@angular/core';
import { AccountV2Service } from 'src/app/services/account-v2-service';

/**
 * Minimal Auction Admin import (Phase 5 Step 2 — first-loop scope, NOT a
 * full admin app). Paste a catalogue as CSV (lot,registration[,guideLow])
 * or JSON, preview PlateX classifications, then import. Import runs the
 * real pipeline: classify → persist → canonical event → interest
 * deliveries. Richer admin (dashboard, history, bulk) comes later.
 */
@Component({
  selector: 'pla-auction-admin',
  templateUrl: './auction-admin.component.html',
  styleUrls: ['./auction-admin.component.scss'],
})
export class AuctionAdminComponent {
  public house = 'dvla';
  public auctionCode = '';
  public title = '';
  public providerModel = 'timed_event';
  public opensAt = '';
  public closesAt = '';
  public rawLots = ''; // CSV or JSON pasted by the admin
  public preview: any = null;
  public importResult: any = null;
  public error: string | null = null;
  public busy = false;

  constructor(private account: AccountV2Service) {}

  /** Parse pasted lots (JSON array, or CSV lines: reg[,lot[,guideLowPounds]]). */
  private parseLots(): { registration: string; lotNumber?: string; guideLowPence?: number }[] {
    const text = (this.rawLots || '').trim();
    if (!text) return [];
    if (text.startsWith('[')) {
      return JSON.parse(text);
    }
    return text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l && !/^lot\b/i.test(l))
      .map((line) => {
        const parts = line.split(',').map((p) => p.trim());
        // Accept "registration", "lot,registration", "lot,registration,guide"
        let reg: string, lot: string | undefined, guide: string | undefined;
        if (parts.length === 1) {
          reg = parts[0];
        } else {
          lot = parts[0];
          reg = parts[1];
          guide = parts[2];
        }
        const out: any = { registration: reg };
        if (lot) out.lotNumber = lot;
        const g = Number((guide || '').replace(/[^0-9.]/g, ''));
        if (isFinite(g) && g > 0) out.guideLowPence = Math.round(g * 100);
        return out;
      });
  }

  private buildRequest(checksumSuffix: string) {
    const lots = this.parseLots();
    // Deterministic checksum from the payload so re-import of the same
    // paste is an idempotent no-op.
    const checksum =
      'manual-' +
      this.auctionCode.trim().toUpperCase() +
      '-' +
      this.hash(JSON.stringify(lots)) +
      checksumSuffix;
    return {
      house: this.house.trim().toLowerCase(),
      auctionCode: this.auctionCode.trim(),
      title: this.title || null,
      providerModel: this.providerModel,
      opensAt: this.opensAt || null,
      closesAt: this.closesAt || null,
      opensAtConfirmed: !!this.opensAt,
      closesAtConfirmed: !!this.closesAt,
      checksum,
      sourceFormat: 'manual',
      lots,
    };
  }

  private hash(s: string): string {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(16);
  }

  public runPreview(): void {
    this.error = null;
    this.importResult = null;
    try {
      this.busy = true;
      this.account.previewCatalogue(this.buildRequest('')).subscribe({
        next: (r) => {
          this.preview = r;
          this.busy = false;
        },
        error: (e) => {
          this.error = e?.error?.rejectedReason || 'Preview failed.';
          this.busy = false;
        },
      });
    } catch (e: any) {
      this.error = 'Could not parse lots: ' + e.message;
      this.busy = false;
    }
  }

  public runImport(): void {
    this.error = null;
    try {
      this.busy = true;
      this.account.importCatalogue(this.buildRequest('')).subscribe({
        next: (r) => {
          this.importResult = r;
          this.busy = false;
        },
        error: (e) => {
          this.error = e?.error?.rejectedReason || 'Import failed.';
          this.busy = false;
        },
      });
    } catch (e: any) {
      this.error = 'Could not parse lots: ' + e.message;
      this.busy = false;
    }
  }
}
